import { useEffect, useRef, useState } from 'react';
import './ThreatsManaged.scss';
import axios from 'axios';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ThreatsProgressBar } from '../../components/operator-dashboard/progress-bar/ThreatsProgressBar';
import { TableWidget } from '../../components/table-widget/TableWidget';
import { ApexOptions } from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import CountUp from 'react-countup';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Bug,
  Check,
  Fingerprint,
  Link2,
  LucideIcon,
  MonitorSmartphone,
  RotateCcw,
  Router,
  ShieldAlert,
  ShieldCheck,
  User,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import '../../design-system/index.css';
import { chartTokens } from '../../design-system/chartTokens';
import { fonts } from '../../design-system/tokens';
import type { BDTheme } from '../../design-system/types';
import { Trans, useTranslation } from 'react-i18next';
import { getLanguage } from '../../util/languages';

interface DataPoint {
  x: Date;
  y: number;
}

export const ThreatsManaged = (props: { darkMode?: boolean }) => {
  const { operatorId, apiUrl } = useParams();
  const navigate = useNavigate();
  // aliased: `t` is already used below for the chart tokens
  const { t: translate, i18n } = useTranslation();
  const [summaryData, setSummaryData] = useState<any>(0);
  const [threatsCount, setThreatsCount] = useState<any>(0);
  const [cardTitle, setCardTitle] = useState<any>(''); // '' = all threats, otherwise a threat category key
  const [cardQty, setCardQty] = useState<any>(0);
  const [prevSelectedBar, setPrevSelectedBar] = useState<any>();
  const [accountsData, setAccountsData] = useState<any>(1);
  const [cardColor, setCardColor] = useState('#000000');
  const [active, setActive] = useState(false);
  const [hour24, setHour24] = useState(false);
  const [tableApiUrl, setTableApiUrl] = useState<any>();
  const [selectedRange, setSelectedRange] = useState<number>(1);
  const [zoomRange, setZoomRange] = useState<number>(selectedRange);
  const [minX, setMinX] = useState<any>(new Date(new Date().getTime() - 24 * 60 * 60 * 1000));
  const [maxX, setMaxX] = useState<any>(new Date());
  const [formatMinX, setFormatMinX] = useState<Date>(minX);
  const [formatMaxX, setFormatMaxX] = useState<Date>(maxX);
  const [zoomLevel, setZoomLevel] = useState<string>('');
  const [selectedBar, setSelectedBar] = useState<any[]>([]);
  const [selectedBarForTable, setSelectedBarForTable] = useState<string>('');
  const [graphData, setGraphData] = useState<any>({});
  const [deviceIds, setDeviceIds] = useState<any>()

  const [deviceSearch, setDeviceSearch] = useState<string>('');
  const [cpeSearch, setCpeSearch] = useState<string>('');
  const [urlSearch, setUrlSearch] = useState<string>('');
  const [accountSearch, setAccountSearch] = useState<string>('');

  const location = useLocation();

  const threatData = [
    {
      label: translate('threatsPage.categories.app_fw'),
      count: accountsData.threatsGroupedByKey?.app_fw !== undefined ? accountsData.threatsGroupedByKey?.app_fw : 0,
      color: '#0088CE',
      key: 'app_fw',
      active: active,
    },
    {
      label: translate('threatsPage.categories.dev_fw'),
      count: accountsData.threatsGroupedByKey?.dev_fw !== undefined ? accountsData.threatsGroupedByKey?.dev_fw : 0,
      color: '#F57C42',
      key: 'dev_fw',
      active: active,
    },
    {
      label: translate('threatsPage.categories.mw_site'),
      count: accountsData.threatsGroupedByKey?.mw_site !== undefined ? accountsData.threatsGroupedByKey?.mw_site : 0,
      color: '#30D5C8',
      key: 'mw_site',
      active: active,
    },
    {
      label: translate('threatsPage.categories.dev_ab'),
      count: accountsData.threatsGroupedByKey?.dev_ab !== undefined ? accountsData.threatsGroupedByKey?.dev_ab : 0,
      color: '#DA70D6',
      key: 'dev_ab',
      active: active,
    },
    {
      label: translate('threatsPage.categories.unauth'),
      count: accountsData.threatsGroupedByKey?.unauth !== undefined ? accountsData.threatsGroupedByKey?.unauth : 0,
      color: '#a057bd',
      key: 'unauth',
      active: active,
    },
    {
      label: translate('threatsPage.categories.app_blk'),
      count: accountsData.threatsGroupedByKey?.app_blk !== undefined ? accountsData.threatsGroupedByKey?.app_blk : 0,
      color: '#D72D6A',
      key: 'app_blk',
      active: active,
    },
    {
      label: translate('threatsPage.categories.data_leak'),
      count: accountsData.threatsGroupedByKey?.data_leak !== undefined ? accountsData.threatsGroupedByKey?.data_leak : 0,
      color: '#CCFF00',
      key: 'data_leak',
      active: active,
    },
    {
      label: translate('threatsPage.categories.dev_vuln'),
      count: accountsData.threatsGroupedByKey?.dev_vuln !== undefined ? accountsData.threatsGroupedByKey?.dev_vuln : 0,
      color: '#9B5B77',
      key: 'dev_vuln',
      active: active,
    },
    {
      label: translate('threatsPage.categories.c2_site'),
      count: accountsData.threatsGroupedByKey?.c2_site !== undefined ? accountsData.threatsGroupedByKey?.c2_site : 0,
      color: '#E8A2B8',
      key: 'c2_site',
      active: active,
    },
    {
      label: translate('threatsPage.categories.ras_site'),
      count: accountsData.threatsGroupedByKey?.ras_site !== undefined ? accountsData.threatsGroupedByKey?.ras_site : 0,
      color: '#ffd900',
      key: 'ras_site',
      active: active,
    },
    {
      label: translate('threatsPage.categories.ph_site'),
      count: accountsData.threatsGroupedByKey?.ph_site !== undefined ? accountsData.threatsGroupedByKey?.ph_site : 0,
      color: '#E30B5C',
      key: 'ph_site',
      active: active,
    },
    {
      label: translate('threatsPage.categories.bot_site'),
      count: accountsData.threatsGroupedByKey?.bot_site !== undefined ? accountsData.threatsGroupedByKey?.bot_site : 0,
      color: '#FF6F61',
      key: 'bot_site',
      active: active,
    },
    {
      label: translate('threatsPage.categories.sp_site'),
      count: accountsData.threatsGroupedByKey?.sp_site !== undefined ? accountsData.threatsGroupedByKey?.sp_site : 0,
      color: '#20B2AA',
      key: 'sp_site',
      active: active,
    },
    {
      label: translate('threatsPage.categories.ad_site'),
      count: accountsData.threatsGroupedByKey?.ad_site !== undefined ? accountsData.threatsGroupedByKey?.ad_site : 0,
      color: '#CCCCFF',
      key: 'ad_site',
      active: active,
    },
    {
      label: translate('threatsPage.categories.fas_site'),
      count: accountsData.threatsGroupedByKey?.fas_site !== undefined ? accountsData.threatsGroupedByKey?.fas_site : 0,
      color: '#FFCC99',
      key: 'fas_site',
      active: active,
    },
  ];

  const [activeStates, setActiveStates] = useState(
    threatData.reduce((acc: any, threat: any) => {
      acc[threat.key] = false; // Set default as inactive
      return acc;
    }, {})
  );

  // Sort the threatData array by count in descending order
  const sortedThreatData = threatData.sort((a, b) => b.count - a.count);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const threatsMitigated = await axios.get(
          `/v2/op/device-threats/${operatorId}?startDate=${minX.toISOString()}&endDate=${maxX.toISOString()}&cpeId=${cpeSearch}&deviceId=${deviceSearch}&account=${accountSearch}&url=${urlSearch}`
        );
        setAccountsData(threatsMitigated.data);
        setSummaryData(threatsMitigated.data.amount);
        setThreatsCount(threatsMitigated.data.amount);
      } catch (error) {
        console.error('Error fetching API data:', error);
        setAccountsData('');
        setSummaryData('');
        setThreatsCount('');
      }
    };

    fetchData();
  }, [selectedRange, zoomRange, minX, maxX, deviceSearch, cpeSearch, urlSearch, accountSearch]);

  useEffect(() => {
    const setupTableApiUrl = () => {
      let url = 'op/' + 'operatorthreats';
      if (operatorId) {
        url += '/' + operatorId;
      }
      setTableApiUrl(url);
    };

    if (location.state !== null) {
      setZoomRange(1);
    }

    setupTableApiUrl();
  }, [location.state, operatorId, graphData]);

  async function fetchTableApi() {
    let url = 'op/' + 'operatorthreats';
    if (operatorId) {
      url += '/' + operatorId;
    }
    setTableApiUrl(url);
  }

  const handleDateRangeChange = (days: number) => {
    setZoomRange(days);
    setSelectedRange(days);
    setMinX(new Date(new Date().getTime() - days * 24 * 60 * 60 * 1000));
    setMaxX(new Date());
  };

  useEffect(() => {}, [cardTitle, cardQty]);

  const colorMapping: any = {
    app_fw: '#0088CE',
    dev_fw: '#F57C42',
    mw_site: '#30D5C8',
    dev_ab: '#DA70D6',
    unauth: '#a057bd',
    app_blk: '#D72D6A',
    data_leak: '#CCFF00',
    dev_vuln: '#9B5B77',
    c2_site: '#E8A2B8',
    ras_site: '#ffd900',
    ph_site: '#E30B5C',
    bot_site: '#FF6F61',
    sp_site: '#20B2AA',
    ad_site: '#CCCCFF',
    fas_site: '#FFCC99',
  };

  useEffect(() => {
    const determineZoomLevel = () => {
      if (zoomRange <= 0.01) {
        return 'minute';
      } else if (zoomRange <= 0.1) {
        return '5min';
      } else if (zoomRange <= 0.5) {
        return '30min';
      } else if (zoomRange <= 1.8) {
        return 'hour';
      } else if (zoomRange <= 20) {
        return 'day';
      } else if (zoomRange <= 30) {
        return 'week';
      } else if (zoomRange <= 180) {
        return 'week';
      }
      return 'day';
    };

    setZoomLevel(determineZoomLevel());
  }, [zoomRange]);

  const [startDate, setStartDate] = useState<any>()
  const [endDate, setEndDate] = useState<any>()
  const zoomLevelRef = useRef<string>(zoomLevel);

  useEffect(() => {
    zoomLevelRef.current = zoomLevel;
  }, [zoomLevel]);

  function formatDrillDownDate(datestring: Date) {
    try {
      const date = new Date(datestring);
      let weekEndDate = new Date(date);
      weekEndDate.setDate(date.getDate() + 6)

      let hours: string;
      let minutes: string;

      let formattedStartDate = `${date.toISOString().split('T')[0]}`;
      let formattedWeekDate = `${weekEndDate.toISOString().split('T')[0]}`

      if (zoomLevelRef.current === 'week') {
        setStartDate(`${formattedStartDate} 00:00:00.000`)
        setEndDate(`${formattedWeekDate} 23:59:59.999`)
      }
      if (zoomLevelRef.current === 'day') {
        setStartDate(`${formattedStartDate} 00:00:00.000`)
        setEndDate(`${formattedStartDate} 23:59:59.999`)
      }
      if (zoomLevelRef.current === 'hour') {      
        hours = String(date.getHours() + 1).padStart(2, '0');
        setStartDate(`${formattedStartDate} ${String(date.getHours()).padStart(2, '0')}:00:00.000`);
        setEndDate(`${formattedStartDate} ${hours}:00:00.000`)
      }
      if (zoomLevelRef.current === '30min') {
        hours = String(date.getHours()).padStart(2, '0');
        minutes = String(date.getMinutes() + 29).padStart(2, '0');
        
        setStartDate(`${formattedStartDate}:00.000`);
        setEndDate(`${formattedStartDate} ${hours}:${minutes}:59.999`)
      }
      if (zoomLevelRef.current === '5min') {
        hours = String(date.getHours()).padStart(2, '0');
        minutes = String(date.getMinutes() + 5).padStart(2, '0');
        
        setStartDate(`${formattedStartDate}:00.000`);
        setEndDate(`${formattedStartDate} ${hours}:${minutes}:00.000`)
      }
      
    } catch (e) {
      console.log(e);
    }
  }

  const chartRef = useRef<any>(null);

  const [chartData, setChartData] = useState({
    series: [
      {
        name: 'Threats',
        data: [] as DataPoint[],
      },
    ],
    options: {
      chart: {
        zoom: {
          type: 'x',
          enabled: true,
          autoScaleYaxis: true,
        },
        toolbar: {
          autoSelected: 'zoom',
          tools: {
            reset: false,
            zoomin: false,
            zoomout: false,
          },
        },
        events: {
          zoomed: (chartContext: any, { xaxis }: any) => zoomIn(chartContext, { xaxis }),
          dataPointSelection: (event, chartContext, opts) => {
            const { seriesIndex, dataPointIndex, w } = opts;
            const clickedData = w.config.series[seriesIndex].data[dataPointIndex];
            const clickedName = w.config.series[seriesIndex].name;

            if (clickedData.length === 3) {
              setDeviceIds(clickedData[2])
              formatDrillDownDate(new Date(clickedData[0]))
              setSelectedBarForTable(clickedName)
            } else {
              setDeviceIds(clickedData.devices)
              formatDrillDownDate(clickedData.x)
              setSelectedBarForTable('')
            }
            setHour24(true)

          },
        },
        height: 350,
        type: 'line',
        background: '#fff',
      },
      colors: ['#003D4C'],
      dataLabels: {
        enabled: false,
        offsetY: 0,
        style: {
          fontSize: '8px',
          colors: ['#304758'],
        },
      },
      xaxis: {
        type: 'datetime',
        max: new Date(maxX).getTime(), // Set maximum date explicitly
        axisBorder: { show: false },
        axisTicks: { show: false },
        crosshairs: {
          fill: {
            type: 'gradient',
            gradient: {
              colorFrom: '#D8E3F0',
              colorTo: '#BED1E6',
              stops: [0, 100],
              opacityFrom: 0.4,
              opacityTo: 0.5,
            },
          },
        },
      },
      markers: {
        size: 4,
        colors: ['#003D4C'],
        strokeColors: '#003D4C',
        strokeWidth: 2,
      },
      tooltip: {
        shared: false,
        intersect: true,
        custom: function ({ series, dataPointIndex }: any) {
          return series
            .map((s: any, index: any) => {
              const value = s[dataPointIndex] !== undefined ? s[dataPointIndex] : 0;
              const color = ['#003D4C'];
              return `
              <div style="display: flex; align-items: center; margin-bottom: 5px;">
                <span style="background-color: ${color}; 
                             width: 10px; 
                             height: 10px; 
                             border-radius: 50%; 
                             display: inline-block; 
                             margin-right: 5px;">
                </span>
                <span style="color: #10182080; font-weight: 650;">${translate('threatsPage.chart.threatsCount', { count: Number(value) || 0 })}</span>
              </div>
            `;
            })
            .join('');
        },
      },
      yaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { show: true },
        grid: { show: false },
      },
      legend: { show: false },
      stroke: { width: 3 },
    } as ApexOptions,
  });

  const zoomInB = () => {
    if (chartRef.current) {
      const chart = chartRef.current.chart;
      const currentMin = chart.w.globals.minX;
      const currentMax = chart.w.globals.maxX;
      const zoomFactor = 0.8; // Set your zoom factor here

      const newMin = currentMin + ((currentMax - currentMin) * (1 - zoomFactor)) / 2;
      const newMax = currentMax - ((currentMax - currentMin) * (1 - zoomFactor)) / 2;

      chart.zoomX(newMin, newMax);
    }
  };

  // Helper function to programmatically zoom out
  const zoomOutB = () => {
    if (chartRef.current) {
      const chart = chartRef.current.chart;
      const currentMin = chart.w.globals.minX;
      const currentMax = chart.w.globals.maxX;
      const zoomFactor = 1.2; // Set your zoom factor here

      const newMin = currentMin - ((currentMax - currentMin) * (zoomFactor - 1)) / 2;
      const newMax = currentMax + ((currentMax - currentMin) * (zoomFactor - 1)) / 2;

      chart.zoomX(newMin, newMax);
    }
  };

  useEffect(() => {
    const currentDate = new Date();

    if (maxX > currentDate) {
      console.log('maxX is in the future. Reverting to current date and time.');
      setMaxX(currentDate);
    }

    const timeDifference = maxX.getTime() - minX.getTime();
    const fiveMinutesInMs = 5 * 60 * 1000;

    if (timeDifference < fiveMinutesInMs) {
      console.log('Time difference is less than 5 minutes. Adjusting minX to be 5 minutes before maxX.');
      setMinX(new Date(maxX.getTime() - fiveMinutesInMs));
    }
  }, [maxX, minX]);

  useEffect(() => {
    if (selectedBar.length >= 1) {
      const fetchBar = async () => {
        if (minX && maxX && zoomLevel) {
          try {
            let newGraphData = { ...graphData };

            for (const key of selectedBar) {
              const response = await axios.get<any[]>(`/v2/op/device-threats/${operatorId}/fetch-chart`, {
                params: {
                  startDate: minX.toISOString(),
                  endDate: maxX.toISOString(),
                  groupBy: zoomLevel,
                  selectedKey: key,
                  cpeId: cpeSearch,
                  deviceId: deviceSearch,
                  url: urlSearch,
                  account: accountSearch,
                },
              });

              newGraphData[key] = response.data;

              let totalQuantity = 0;
              selectedBar.forEach((key) => {
                if (key in accountsData.threatsGroupedByKey) {
                  totalQuantity += accountsData.threatsGroupedByKey[key];
                }
              });

              setCardQty(totalQuantity);

              const formattedData: DataPoint[] = response.data.map((item) => {
                const [date, value, devices] = item;
                return {
                  x: new Date(date),
                  y: value,
                  devices: devices
                };
              });

              const displayDates = formattedData.map((point) => point.x).slice(0, 5); // Adjust slicing as needed

              setChartData((prevState) => {
                const existingSeriesIndex = prevState.series.findIndex((series) => series.name === key);
                if (existingSeriesIndex >= 0) {
                  const updatedSeries = [...prevState.series];

                  updatedSeries[existingSeriesIndex] = {
                    ...updatedSeries[existingSeriesIndex],
                    data: formattedData,
                  };
                  return {
                    ...prevState,
                    series: updatedSeries,
                  };
                } else {
                  return {
                    ...prevState,
                    options: {
                      ...prevState.options,
                      xaxis: {
                        ...prevState.options.xaxis,
                        categories: displayDates, // Set the categories to only the 5 specific dates
                        max: new Date(maxX).getTime(),

                        labels: {
                          formatter: (value: string) => {
                            const date = new Date(value);
                            return `${date.getDate()}-${date.toLocaleString(getLanguage(i18n.language).locale, { month: 'short' })}-${date.getFullYear()}`;
                          },
                        },
                      },
                    },
                    series: [
                      ...prevState.series,
                      {
                        name: key,
                        data: formattedData,
                      },
                    ],
                  };
                }
              });
            }

            setGraphData(newGraphData);
          } catch (error) {
            setChartData((prevState) => ({
              ...prevState,
              series: [],
            }));
            console.error('Error fetching data', error);
          }
        }
      };
      fetchBar();
    } else {
      const fetchBar = async () => {
        if (minX && maxX && zoomLevel) {
          try {
            const response = await axios.get<any[]>(`/v2/op/device-threats/${operatorId}/fetch-chart`, {
              params: {
                startDate: minX.toISOString(),
                endDate: maxX.toISOString(),
                groupBy: zoomLevel,
                cpeId: cpeSearch,
                deviceId: deviceSearch,
                url: urlSearch,
                account: accountSearch,
              },
            });

            setGraphData(response.data);
            setCardQty(accountsData.amount);

            const formattedData: DataPoint[] = response.data.map((item) => {
              const [date, value, devices] = item;
              return {
                x: new Date(date),
                y: value,
                devices: devices
              };
            });

            setChartData((prevState) => ({
              ...prevState,
              options: {
                ...prevState.options,
                xaxis: {
                  ...prevState.options.xaxis,
                  max: new Date(maxX).getTime(),
                },
              },
              series: [
                {
                  ...prevState.series[0],
                  data: formattedData,
                },
              ],
            }));
          } catch (error) {
            setChartData((prevState) => ({
              ...prevState,
              series: [],
            }));
            console.error('Error fetching data');
          }
        }
      };
      fetchBar();
    }
  }, [minX, maxX, zoomLevel, selectedBar, accountsData, deviceSearch, cpeSearch, urlSearch, accountSearch]);

  const zoomIn = (chartContext: any, { xaxis }: any) => {
    if (xaxis.min && xaxis.max) {
      const durationMs = xaxis.max - xaxis.min;
      const oneDayInMs = 24 * 60 * 60 * 1000;
      const durationInDays = durationMs / oneDayInMs;
      setZoomRange(durationInDays);

      let minDate = new Date(xaxis.min);
      let maxDate = new Date(xaxis.max);

      setMinX(minDate);
      setMaxX(maxDate);
    }
  };

  const addSelectedBar = (newKey: any) => {
    setSelectedBar((prevBars) => {
      if (!prevBars.includes(newKey)) {
        return [...prevBars, newKey];
      }
      return prevBars;
    });
  };

  const removeSelectedBar = (barName: string) => {
    setSelectedBar((prevBars) => prevBars.filter((key) => key !== barName));
  };

  console.log(active);

  const handleProgressBarClick = (label: string, value: number, barName: string) => {
    if (!selectedBar.includes(barName)) {
      if (selectedBar.length === 0) {
        setCardTitle(barName);
      } else {
        setCardTitle('');
      }

      setActiveStates((prevStates: any) => ({
        ...prevStates,
        [barName]: true,
      }));

      addSelectedBar(barName);
      setSummaryData(value);
      setCardColor('#0088CE');
    } else {
      setCardTitle('');
      removeSelectedBar(barName);

      setActiveStates((prevStates: any) => ({
        ...prevStates,
        [barName]: false,
      }));

      setPrevSelectedBar(barName);
      setCardColor('#000000');
      setChartData((prevState) => ({
        ...prevState,
        options: {
          ...prevState.options,
          colors: ['#003D4C'],
          dataLabels: {
            enabled: false,
            offsetY: 0,
            style: {
              fontSize: '8px',
              colors: ['#304758'],
            },
          },
          markers: {
            size: 4,
            colors: ['#003D4C'],
            strokeColors: '#003D4C',
            strokeWidth: 2,
          },
          tooltip: {
            ...prevState.options.tooltip,
            custom: function ({ series, dataPointIndex }: any) {
              return series
                .map((s: any, index: any) => {
                  const value = s[dataPointIndex] !== undefined ? s[dataPointIndex] : 0;
                  const color = ['#003D4C'];
                  return `
                  <div style="display: flex; align-items: center; margin-bottom: 5px;">
                    <span style="background-color: ${color}; 
                                 width: 10px; 
                                 height: 10px; 
                                 border-radius: 50%; 
                                 display: inline-block; 
                                 margin-right: 5px;">
                    </span>
                    <span style="color: #10182080; font-weight: 650;">${translate('threatsPage.chart.threatsCount', { count: Number(value) || 0 })}</span>
                  </div>
                `;
                })
                .join('');
            },
          },
          xaxis: {
            ...prevState.options.xaxis,
            max: new Date(maxX).getTime(),
          },
        },
      }));
    }
  };

  useEffect(() => {
    if (selectedBar.length >= 1) {
      const newSeries = selectedBar.map((barKey) => ({
        name: barKey,
        data: graphData[barKey] || [],
      }));

      const newColors = selectedBar.map((barKey) => colorMapping[barKey] || '#003D4C');
      setChartData((prevState) => ({
        ...prevState,
        series: newSeries,
        options: {
          ...prevState.options,
          colors: newColors,
          xaxis: {
            ...prevState.options.xaxis,
            max: new Date(maxX).getTime(),
          },
          markers: {
            size: 4,
            colors: newColors,
            strokeColors: newColors,
            strokeWidth: 2,
          },
          tooltip: {
            ...prevState.options.tooltip,
            custom: function ({ series, dataPointIndex }) {
              return series
                .map((s: any, index: any) => {
                  const value = s[dataPointIndex] !== undefined ? s[dataPointIndex] : 0;
                  const color = newColors[index];
                  const label = selectedBar[index]
                    ? translate(`threatsPage.categories.${selectedBar[index]}`, { defaultValue: selectedBar[index] })
                    : translate('threatsPage.chart.threats');
                  return `
                  <div style="display: flex; align-items: center; margin-bottom: 5px;">
                    <span style="background-color: ${color}; 
                                 width: 10px; 
                                 height: 10px; 
                                 border-radius: 50%; 
                                 display: inline-block; 
                                 margin: 10px">
                    </span>
                    <span style="color: #10182080; margin-right: 15px; font-weight: 650;">${value} ${label}</span>
                  </div>
                `;
                })
                .join('');
            },
          },
        },
      }));
    }
  }, [selectedBar, graphData]);

  const handleSelectedRangeChange = (selectedRange: number) => {};

  const nonSelected =
    'flex w-full justify-center items-center text-sm py-1 border-1 bg-[#BDCDD1] border-[#003D4C] h-[40px] font-bold text-[#10182080]';
  const selected =
    'flex w-full options justify-center items-center text-sm py-1 border-1 bg-[#53B798] border-[#003D4C] h-[40px] font-bold text-[#101820]';

  function goToCpePage(rowData: any) {
    navigate(`/operators/${operatorId}/cpe/${rowData.key.ID}`);
  }

  function formatDate(date: Date): string {
    if (!(date instanceof Date)) {
      throw new Error('Input must be a Date object');
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString(getLanguage(i18n.language).locale, { month: 'short' }).toUpperCase();
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }

  function formatTime(date: Date): string {
    if (!(date instanceof Date)) {
      throw new Error('Input must be a Date object');
    }

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${hours}:${minutes}`;
  }

  // this is why hour is -1h //
  const newDate = () => {
    // if (graphData.length > 0) {
    //   setFormatMinX(new Date(new Date(graphData[0][0]).getTime() - 60 * 60 * 1000));
    //   setFormatMaxX(new Date(new Date(graphData[graphData.length - 1][0]).getTime() - 60 * 60 * 1000));
    // }
    // else {
    setFormatMinX(new Date(minX - 60 * 60 * 1000));
    setFormatMaxX(new Date(maxX - 60 * 60 * 1000));
    // }
  };

  const [cpeData, setCpeData] = useState<number>(0);
  const [deviceData, setDeviceData] = useState<number>(0);
  const [threatsData, setThreatsData] = useState<number>(0);
  const [dnsData, setDnsData] = useState<number>(0);
  const [uniqueBlockedData, setUniqueBlockedData] = useState<number>(0);
  const [cveCount, setCveCount] = useState<any>();
  const [highestRiskScore, setHighestRiskScore] = useState<number>(0);
  const [deviceHighestRiskScore, setDeviceHighestRiskScore] = useState<number>(0);

  const fetchWidgetData = async () => {
    try {
      const response = await axios.get(`/v2/op/master-operator/${operatorId}/global-counts`);
      setCpeData(response.data.data[0].cpeCount ? response.data.data[0].cpeCount : 0);
      setDeviceData(response.data.data[0].devicesCount ? response.data.data[0].devicesCount : 0);
      setThreatsData(response?.data?.data[0]?.past24HourThreats);
      setDnsData(response.data.data[0].past24HourDns ? response.data.data[0].past24HourDns : 0);
      setCveCount(response.data.data[0].cveCount ? response.data.data[0].cveCount : 0);
      setHighestRiskScore(response.data.data[0].formatHighestRiskScore.highestRiskScore ? response.data.data[0].formatHighestRiskScore.highestRiskScore : 0);
      setDeviceHighestRiskScore(response.data.data[0].formatHighestRiskScore.deviceId ? response.data.data[0].formatHighestRiskScore.deviceId : 0);
      setUniqueBlockedData(response.data.data[0].past24HoursUniqueBlocked ? response.data.data[0].past24HoursUniqueBlocked : 0)
console.log('response?.data?.data[0]?.past24HourThreats :', response?.data?.data[0]?.past24HourThreats );
    } catch (error) {
      console.error('Error fetching widget data:', error);
    }
  };
  
  useEffect(() => {
    fetchWidgetData()
  }, [operatorId])
  useEffect(() => {
    newDate();
  }, [graphData, minX, maxX, selectedBar, selectedRange, zoomRange, zoomLevel]);

  const theme: BDTheme = props.darkMode ? 'dark' : 'light';
  const t = chartTokens[theme];
  const LEGACY_SERIES_COLOR = '#003D4C';

  // Theme the chart at render time; chartData.options is merged in many places above,
  // so swap the legacy default series colour and surface colours here instead.
  const themedChartOptions: ApexOptions = (() => {
    const o: any = chartData.options;
    const swap = (colors: any) =>
      Array.isArray(colors) ? colors.map((c: string) => (c === LEGACY_SERIES_COLOR ? t.accent : c)) : colors;
    return {
      ...o,
      chart: { ...o.chart, background: 'transparent', foreColor: t.mute, fontFamily: fonts.sans },
      theme: { mode: theme },
      colors: swap(o.colors),
      markers: { ...o.markers, colors: swap(o.markers?.colors), strokeColors: swap(o.markers?.strokeColors) },
      grid: { borderColor: t.border, strokeDashArray: 3 },
      xaxis: {
        ...o.xaxis,
        labels: { ...o.xaxis?.labels, style: { colors: t.mute, fontSize: '11px' } },
        crosshairs: { ...o.xaxis?.crosshairs, fill: { type: 'solid', color: t.track } },
      },
      yaxis: { ...o.yaxis, labels: { ...o.yaxis?.labels, style: { colors: t.mute, fontFamily: fonts.mono } } },
    };
  })();

  const rangeOptions = [
    { days: 1, label: translate('threatsPage.range.today') },
    { days: 7, label: translate('threatsPage.range.past7Days') },
    { days: 30, label: translate('threatsPage.range.past30Days') },
  ];

  const filterFields = [
    { id: 'account', type: 'text', placeholder: translate('threatsPage.filters.account'), icon: User, onSet: setAccountSearch },
    { id: 'url', type: 'text', placeholder: 'URL', icon: Link2, onSet: setUrlSearch },
    { id: '7-days-cpe', type: 'number', placeholder: 'CPE', icon: Router, onSet: setCpeSearch },
    { id: '7-days-device', type: 'number', placeholder: translate('threatsPage.filters.device'), icon: MonitorSmartphone, onSet: setDeviceSearch },
  ];

  type Kpi = { title: string; value: number; icon: LucideIcon; tone?: string; linkUrl?: string; linkedpage?: string };
  const kpis: Kpi[] = [
    { title: translate('threatsPage.kpi.threatsBlockedToday'), value: threatsData, icon: ShieldCheck },
    { title: translate('threatsPage.kpi.uniqueThreatsBlockedToday'), value: uniqueBlockedData || 0, icon: Fingerprint },
    { title: translate('threatsPage.kpi.cveCriticalHigh'), value: cveCount || 0, icon: Bug, tone: 'warn' },
    {
      title: translate('threatsPage.kpi.highestRiskScore'),
      value: highestRiskScore || 0,
      icon: ShieldAlert,
      tone: 'danger',
      linkUrl: `/operators/${operatorId}/cpe/2/d/${deviceHighestRiskScore}/devicesPage`,
      linkedpage: 'Threats',
    },
  ];

  // Same behaviour as WidgetBox: navigate to linkUrl and remember the nav tab
  const openKpi = (kpi: Kpi) => {
    if (!kpi.linkUrl) return;
    navigate(kpi.linkUrl, { state: { key: kpi.linkedpage, timestamp: new Date().toISOString() } });
    if (kpi.linkedpage) window.localStorage.setItem('currentNav', kpi.linkedpage);
  };

  const totalThreats = accountsData?.amount || 0;
  const selectedCount = selectedBar.length;

  return (
    <div className="bd tm-page" data-theme={theme} data-accent="mint">
      <div className="tm-wrap">
        <header className="tm-hd">
          <span className="tm-eyebrow">{translate('threatsPage.eyebrow')}</span>
          <div className="tm-hd-title">
            <CountUp className="bd-stat-num tm-hd-num" preserveValue={true} end={threatsData || 0} duration={2} separator="," />
            <h1 className="bd-h1 tm-title">
              {cardTitle ? translate(`threatsPage.categories.${cardTitle}`) : translate('threatsPage.threatsBlocked')}
            </h1>
          </div>
          <span className="bd-body">{translate('threatsPage.description')}</span>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {kpis.map((kpi) => {
            const Icon = kpi.icon;
            const interactive = !!kpi.linkUrl;
            return (
              <div
                key={kpi.title}
                className={`bd-card bd-kpi ${kpi.tone || ''} ${interactive ? 'interactive' : ''}`}
                role={interactive ? 'button' : undefined}
                tabIndex={interactive ? 0 : undefined}
                onClick={interactive ? () => openKpi(kpi) : undefined}
                onKeyDown={interactive ? (e) => { if (e.key === 'Enter') openKpi(kpi); } : undefined}
              >
                <span className="bd-kpi-icon">
                  <Icon size={18} strokeWidth={1.75} />
                </span>
                <div className="bd-kpi-text">
                  <span className="bd-kpi-label" title={kpi.title}>{kpi.title}</span>
                  <CountUp className="bd-stat-num bd-kpi-num" preserveValue={true} end={Number(kpi.value) || 0} duration={2} separator="," />
                </div>
                {interactive && <ArrowUpRight size={16} className="bd-kpi-arrow" />}
              </div>
            );
          })}
        </div>

        <div className="tm-grid">
          {/* Left Column: summary, range, filters, categories */}
          <aside className="bd-card tm-panel">
            <div className="tm-panel-hd">
              <div className="tm-panel-title">
                <h2 className="bd-h3">{translate('threatsPage.summary.title')}</h2>
                <span className="bd-meta">{translate('threatsPage.summary.subtitle')}</span>
              </div>
              {!hour24 && (
                <div className="bd-segmented tm-range" role="group" aria-label={translate('threatsPage.range.ariaLabel')}>
                  {rangeOptions.map((range) => (
                    <button
                      key={range.days}
                      type="button"
                      className={selectedRange === range.days ? 'active' : ''}
                      aria-pressed={selectedRange === range.days}
                      onClick={() => handleDateRangeChange(range.days)}
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="tm-filters">
              {filterFields.map((field) => {
                const Icon = field.icon;
                return (
                  <label key={field.id} className="bd-search tm-filter">
                    <Icon size={14} />
                    <input
                      type={field.type}
                      id={field.id}
                      placeholder={field.placeholder}
                      aria-label={translate('threatsPage.filters.filterBy', { field: field.placeholder })}
                      onBlur={(e) => field.onSet(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                      }}
                    />
                  </label>
                );
              })}
            </div>

            <div className="tm-cats-hd">
              <span className="bd-field-label">{translate('threatsPage.categoriesTitle')}</span>
              <span className="bd-meta">
                {selectedCount > 0 ? (
                  <span className="bd-chip mint">{translate('threatsPage.selectedCount', { count: selectedCount })}</span>
                ) : (
                  <Trans
                    i18nKey="threatsPage.total"
                    values={{ total: Number(totalThreats).toLocaleString(getLanguage(i18n.language).locale) }}
                    components={{ num: <span className="bd-mono" /> }}
                  />
                )}
              </span>
            </div>

            <div className="tm-cats">
              {threatData.map((threat) => {
                const isOn = !!activeStates[threat.key];
                const pct = threat.count !== undefined && totalThreats ? Number(((threat.count / totalThreats) * 100).toFixed(0)) : 0;
                return (
                  <button
                    key={threat.key}
                    type="button"
                    className={`tm-cat ${isOn ? 'on' : ''} ${!threat.count ? 'empty' : ''}`}
                    style={{ '--cat': threat.color } as React.CSSProperties}
                    aria-pressed={isOn}
                    onClick={() => handleProgressBarClick(threat.label, threat.count, threat.key)}
                  >
                    <span className="tm-cat-check">{isOn && <Check size={11} strokeWidth={3} />}</span>
                    <span className="tm-cat-body">
                      <span className="tm-cat-row">
                        <span className="tm-cat-label">{threat.label}</span>
                        <span className="tm-cat-count bd-mono">{(threat.count ?? 0).toLocaleString(getLanguage(i18n.language).locale)}</span>
                      </span>
                      <span className="tm-cat-bar">
                        <i style={{ width: `${Math.min(pct, 100)}%` }} />
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Right Column: chart or drill-down table */}
          <section className="bd-card tm-chart">
            {!hour24 ? (
              <>
                <div className="tm-chart-hd">
                  <div className="tm-range-display">
                    <span className="bd-meta">{translate('threatsPage.chart.from')}</span>
                    <span className="bd-mono tm-date">
                      {formatTime(formatMinX)} · {formatDate(formatMinX)}
                    </span>
                    <ArrowRight size={14} className="tm-range-arrow" />
                    <span className="bd-meta">{translate('threatsPage.chart.to')}</span>
                    <span className="bd-mono tm-date">
                      {formatTime(formatMaxX)} · {formatDate(formatMaxX)}
                    </span>
                  </div>
                  <div className="tm-tools">
                    <button type="button" className="bd-iconbtn tm-tool" onClick={zoomInB} title={translate('threatsPage.chart.zoomIn')} aria-label={translate('threatsPage.chart.zoomIn')}>
                      <ZoomIn size={16} />
                    </button>
                    <button type="button" className="bd-iconbtn tm-tool" onClick={zoomOutB} title={translate('threatsPage.chart.zoomOut')} aria-label={translate('threatsPage.chart.zoomOut')}>
                      <ZoomOut size={16} />
                    </button>
                    <button
                      type="button"
                      className="bd-iconbtn tm-tool"
                      onClick={() => handleDateRangeChange(selectedRange)}
                      title={translate('threatsPage.chart.resetZoom')}
                      aria-label={translate('threatsPage.chart.resetZoom')}
                    >
                      <RotateCcw size={15} />
                    </button>
                  </div>
                </div>
                <span className="bd-meta tm-chart-hint">{translate('threatsPage.chart.hint')}</span>
                <ReactApexChart
                  key={theme}
                  ref={chartRef}
                  options={themedChartOptions}
                  series={chartData.series}
                  type="line"
                  height={430}
                />
              </>
            ) : (
              <div className="tm-drill">
                <button type="button" className="bd-pill tm-back" onClick={() => setHour24(false)}>
                  <ArrowLeft size={14} />
                  {translate('threatsPage.chart.backToChart')}
                </button>
                <TableWidget
                  defaultDate={'1d'}
                  hasSearch={true}
                  startDate={startDate}
                  endDate={endDate}
                  deviceId={deviceIds}
                  selectedKey={selectedBarForTable}
                  urlSearch={urlSearch}
                  key={tableApiUrl}
                  url={tableApiUrl}
                  countPerPage={6}
                  title={translate('threatsPage.chart.threatDetails')}
                  searchPositionRight={true}
                  hideTimes={true}
                  isDns={true}
                />
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};
